'use client';
import { Button, type ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { UI_CONFIG } from '@eliadmin/core/src/constants/ui-config';
import { cn, mergeClassObjects } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';
import { ChevronLeft as LuChevronLeft, ChevronRight as LuChevronRight } from 'lucide-react';

export type CarouselControlsProps = {
  className?: { main?: string; prev?: string; next?: string; button?: string };
  buttonProps?: ButtonProps;
  prevIcon?: React.ReactNode;
  nextIcon?: React.ReactNode;
};

function ControlsFrame(props: CarouselControlsProps & {
  canGoPrev?: boolean;
  canGoNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  prevLabel?: string;
  nextLabel?: string;
  placeholder?: boolean;
}) {
  const className = mergeClassObjects(props.className, UI_CONFIG.carouselControls?.className);
  const buttonProps = props.buttonProps ?? UI_CONFIG.carouselControls?.buttonProps;
  const prevIcon = props.prevIcon ?? UI_CONFIG.carouselControls?.prevIcon ?? <LuChevronLeft />;
  const nextIcon = props.nextIcon ?? UI_CONFIG.carouselControls?.nextIcon ?? <LuChevronRight />;

  return (
    <div
      className={cn('flex shrink-0 flex-row gap-2', 'ea-carousel-controls', className.main)}
      aria-hidden={props.placeholder || undefined}
    >
      <Button
        {...buttonProps}
        type="button"
        onClick={props.onPrev}
        aria-label={props.prevLabel}
        disabled={!props.canGoPrev}
        className={cn(
          'cursor-pointer border-border',
          'ea-carousel-controls__button ea-carousel-controls__button--prev',
          className.button,
          className.prev,
        )}
        variant="outline"
        size="icon"
      >
        {prevIcon}
      </Button>
      <Button
        {...buttonProps}
        type="button"
        onClick={props.onNext}
        aria-label={props.nextLabel}
        disabled={!props.canGoNext}
        className={cn(
          'cursor-pointer border-border',
          'ea-carousel-controls__button ea-carousel-controls__button--next',
          className.button,
          className.next,
        )}
        variant="outline"
        size="icon"
      >
        {nextIcon}
      </Button>
    </div>
  );
}

// Same geometry, no provider or translation query needed inside a Suspense fallback.
export const CarouselControlsPlaceholder = (props: CarouselControlsProps) => (
  <ControlsFrame {...props} placeholder />
);

export const CarouselControls = (props: CarouselControlsProps) => {
  const { t } = useTranslations();
  const context = useCarouselContext();
  if (!context) throw new Error('CarouselControls must be used inside CarouselProvider');

  return (
    <ControlsFrame
      {...props}
      canGoPrev={context.canGoPrev}
      canGoNext={context.canGoNext}
      onPrev={() => context.api?.scrollPrev()}
      onNext={() => context.api?.scrollNext()}
      prevLabel={t('text3525')}
      nextLabel={t('text3526')}
    />
  );
};
